import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  DatePicker, Form, Input, Select, Slider, Spin, message,
} from 'antd';
import { Plus } from 'lucide-react';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { brandApi, enumApi, opportunityApi, planVersionApi } from '../api/services';
import type {
  OpportunityAttachmentVO,
  PlanBoardVO,
  PlanProjectItem,
  PlanVersionDetail,
  PlanVersionListItem,
  OpportunityVO,
} from '../api/ipc/opportunity';
import { resolveAttachmentDownloadUrl } from '../api/ipc/opportunity';
import { PlanLaunchLane } from './PlanLaunchLane';
import {
  IpcButton,
  IpcFilterToolbar,
  IpcFormField,
  IpcModal,
  IpcModuleTabs,
  IpcPageHeader,
  IpcRowActions,
  IpcGradeTag,
  IpcStatusBadge,
  IpcTable,
  type IpcFilterChipItem,
  type IpcStatusBadgeVariant,
  IPC_FILTER_MULTI_SELECT_PROPS,
} from '../components/ui';
import { OpportunityAttachmentCell } from '../components/OpportunityAttachmentCell';
import { PlanningLinkStatusHover } from '../components/PlanningLinkStatusHover';
import './OpportunityPlanning.css';

const STAGE_COLOR: Record<string, string> = {
  待立项: '#94A3B8',
  进行中: '#3B82F6',
  已完成: '#15803D',
  已终止: '#B42318',
};

const PICK_STATUS_TABS = ['待立项', '进行中', '已完成', '已终止', '全部'] as const;

type PlanBoardNode = PlanBoardVO['nodes'][number];

type BoardFilters = {
  monthFrom?: string;
  monthTo?: string;
  grades?: string[];
  stages?: string[];
  delay?: 'yes' | 'no';
  ownership?: 'in' | 'out';
};

function planStateVariant(state: string): IpcStatusBadgeVariant {
  if (state === '已发布') return 'success';
  if (state === '编制中') return 'warning';
  return 'muted';
}

function coverYears(year: number) {
  return `${year} / ${year + 1} / ${year + 2}`;
}

function emptyBoardFilters(): BoardFilters {
  return {
    monthFrom: undefined,
    monthTo: undefined,
    grades: undefined,
    stages: undefined,
    delay: undefined,
    ownership: undefined,
  };
}

function filterBoardNodes(nodes: PlanBoardNode[], f: BoardFilters): PlanBoardNode[] {
  return (nodes || []).filter(n => {
    const axis = (n.axisMonth || (n.commitDate ? String(n.commitDate).slice(0, 7) : '')).slice(0, 7);
    if (f.monthFrom && (!axis || axis < f.monthFrom)) return false;
    if (f.monthTo && (!axis || axis > f.monthTo)) return false;
    if (f.grades?.length && !f.grades.includes(n.grade)) return false;
    if (f.stages?.length && !f.stages.includes(n.stage)) return false;
    if (f.delay === 'yes' && !(n.delayMonths > 0)) return false;
    if (f.delay === 'no' && (n.delayMonths > 0)) return false;
    if (f.ownership === 'in' && n.isNew) return false;
    if (f.ownership === 'out' && !n.isNew) return false;
    return true;
  });
}

function parseYmIndex(ym?: string | null): number | null {
  const m = String(ym || '').match(/^(\d{4})-(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 12 + parseInt(m[2], 10) - 1;
}

/** 与泳道一致的时间轴跨度 key；跨度变化时用于触发「铺满适配」 */
function laneTimeSpanKey(
  nodes: PlanBoardNode[],
  monthFrom?: string,
  monthTo?: string,
): string {
  const indices = nodes
    .map(n => parseYmIndex(
      (n.axisMonth || (n.commitDate ? String(n.commitDate).slice(0, 7) : '')).slice(0, 7),
    ))
    .filter((x): x is number => x != null);
  if (!indices.length) return 'empty';
  const dataMin = Math.min(...indices);
  const dataMax = Math.max(...indices);
  const start = parseYmIndex(monthFrom) ?? dataMin;
  const end = parseYmIndex(monthTo) ?? dataMax;
  const lo = Math.min(start, end);
  const hi = Math.max(start, end);
  return `${lo}:${hi}`;
}

function calcBoardStats(nodes: PlanBoardNode[]) {
  return {
    inProgress: nodes.filter(n => n.stage === '进行中').length,
    completed: nodes.filter(n => n.stage === '已完成').length,
    terminated: nodes.filter(n => n.stage === '已终止').length,
    notStarted: nodes.filter(n => n.stage === '待立项').length,
    delayed: nodes.filter(n => (n.delayMonths || 0) > 0).length,
    outOfPlan: nodes.filter(n => !!n.isNew).length,
  };
}

const ThreeYearPlan: FC = () => {
  const navigate = useNavigate();
  const { planId: planIdParam } = useParams<{ planId?: string }>();
  const planId = planIdParam && /^\d+$/.test(planIdParam) ? Number(planIdParam) : null;

  const nowYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingPlan, setExportingPlan] = useState(false);
  const [list, setList] = useState<PlanVersionListItem[]>([]);
  const [detail, setDetail] = useState<PlanVersionDetail | null>(null);
  const [board, setBoard] = useState<PlanBoardVO | null>(null);
  const [boardFilters, setBoardFilters] = useState<BoardFilters>(emptyBoardFilters);
  /** 泳道时间轴缩放百分比，100 = 铺满 */
  const [laneZoomPct, setLaneZoomPct] = useState(100);
  const [detailTab, setDetailTab] = useState('board');
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [brands, setBrands] = useState<{ label: string; value: string }[]>([]);
  const [grades, setGrades] = useState<string[]>(['S', 'A', 'B', 'C', 'D', '待定']);
  const [productTypes, setProductTypes] = useState<string[]>(['新品', '升级', '礼盒', 'GWP', '规格衍生']);
  const [molds, setMolds] = useState<string[]>(['开模', '公模', '待定']);
  const [regs, setRegs] = useState<string[]>(['否', '是']);
  const [sources, setSources] = useState<string[]>(['日常', '规划']);
  const [form] = Form.useForm();
  const watchBrand = Form.useWatch('brand', form) as string | undefined;
  const planYear = Form.useWatch('planYear', form) as number | undefined;
  const [items, setItems] = useState<Partial<PlanProjectItem>[]>([]);
  const [pickOpen, setPickOpen] = useState(false);
  const [pickRows, setPickRows] = useState<OpportunityVO[]>([]);
  const [pickSelected, setPickSelected] = useState<number[]>([]);
  const [pickStatusTab, setPickStatusTab] = useState<(typeof PICK_STATUS_TABS)[number]>('待立项');
  const [pickBrand, setPickBrand] = useState('');
  const coverYear = planYear || nowYear;

  const [draftBrand, setDraftBrand] = useState<string>();
  const [draftState, setDraftState] = useState<string>();
  const [appliedBrand, setAppliedBrand] = useState<string>();
  const [appliedState, setAppliedState] = useState<string>();

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      setList(await planVersionApi.getList());
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const detailReqSeq = useRef(0);

  const loadDetail = useCallback(async (id: number, opts?: { resetBoardUi?: boolean }) => {
    const seq = ++detailReqSeq.current;
    setLoading(true);
    try {
      const [d, b] = await Promise.all([planVersionApi.getDetail(id), planVersionApi.getBoard(id)]);
      if (seq !== detailReqSeq.current) return;
      setDetail(d);
      setBoard(b);
      if (opts?.resetBoardUi) {
        setBoardFilters(emptyBoardFilters());
        setLaneZoomPct(100);
        setDetailTab('board');
      }
    } catch (e: any) {
      if (seq !== detailReqSeq.current) return;
      message.error(e?.message || '加载详情失败');
      setDetail(null);
      setBoard(null);
      navigate('/planning', { replace: true });
    } finally {
      if (seq === detailReqSeq.current) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void brandApi.getList().then(b => setBrands((b || []).map(x => ({ label: x.name || x.code, value: x.name || x.code }))));
    void Promise.all([
      enumApi.getList('IPC_OPP_GRADE'),
      enumApi.getList('IPC_OPP_PTYPE'),
      enumApi.getList('IPC_OPP_MOLD'),
      enumApi.getList('IPC_OPP_REG'),
      enumApi.getList('IPC_OPP_SOURCE'),
    ]).then(([g, pt, m, c, s]) => {
      if (g.list?.length) setGrades(g.list.map(i => i.value));
      if (pt.list?.length) setProductTypes(pt.list.map(i => i.value));
      if (m.list?.length) setMolds(m.list.map(i => i.value));
      if (c.list?.length) setRegs(c.list.map(i => i.value));
      if (s.list?.length) setSources(s.list.map(i => i.value));
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (planIdParam && planId == null) {
      message.error('规划 ID 无效');
      navigate('/planning', { replace: true });
    }
  }, [planIdParam, planId, navigate]);

  useEffect(() => {
    if (planId != null) {
      void loadDetail(planId, { resetBoardUi: true });
      return;
    }
    detailReqSeq.current += 1;
    setDetail(null);
    setBoard(null);
    void loadList();
  }, [planId, loadDetail, loadList]);

  const openDetail = (id: number) => {
    navigate(`/planning/${id}`);
  };

  const backToList = () => {
    navigate('/planning');
  };

  const filteredList = useMemo(() => {
    return (list || []).filter(x => {
      if (appliedBrand && x.brand !== appliedBrand) return false;
      if (appliedState && x.state !== appliedState) return false;
      return true;
    });
  }, [list, appliedBrand, appliedState]);

  const applyFilters = (brand?: string, state?: string) => {
    setDraftBrand(brand);
    setDraftState(state);
    setAppliedBrand(brand);
    setAppliedState(state);
  };

  const submitFilters = () => {
    applyFilters(draftBrand, draftState);
  };

  const resetFiltersToDefault = () => {
    applyFilters(undefined, undefined);
  };

  const appliedChips: IpcFilterChipItem[] = useMemo(() => {
    const chips: IpcFilterChipItem[] = [];
    if (appliedBrand) chips.push({ key: 'brand', label: `品牌: ${appliedBrand}` });
    if (appliedState) chips.push({ key: 'state', label: `编制状态: ${appliedState}` });
    return chips;
  }, [appliedBrand, appliedState]);

  const removeChip = (key: string) => {
    const nextBrand = key === 'brand' ? undefined : appliedBrand;
    const nextState = key === 'state' ? undefined : appliedState;
    applyFilters(nextBrand, nextState);
  };

  const openCreate = () => {
    setEditId(null);
    form.setFieldsValue({ brand: brands[0]?.value, planYear: nowYear });
    setItems([]);
    setEditOpen(true);
  };

  const openEdit = async (id: number, state: string) => {
    if (state !== '编制中') {
      message.warning(`该版本已${state}，不可维护`);
      return;
    }
    try {
      const d = await planVersionApi.getDetail(id);
      setEditId(id);
      form.setFieldsValue({ brand: d.brand, planYear: d.planYear });
      setItems((d.items || []).filter(x => !x.isNew).map(x => ({
        id: x.id,
        name: x.name,
        brand: x.brand || d.brand,
        productLine: x.productLine,
        category: x.category,
        productType: x.productType,
        targetInboundDate: x.targetInboundDate,
        grade: x.grade,
        plannedSkuCount: x.plannedSkuCount,
        suggestedBriefDate: x.suggestedBriefDate,
        moldType: x.moldType,
        needCert: x.needCert,
        sourceType: x.sourceType,
        remark: x.remark,
        attachments: x.attachments || [],
        attachmentCount: x.attachmentCount ?? x.attachments?.length ?? 0,
        sourceOpportunityId: x.sourceOpportunityId,
      })));
      setEditOpen(true);
    } catch (e: any) {
      message.error(e?.message || '加载失败');
    }
  };

  const closeEdit = () => {
    setEditOpen(false);
    setPickOpen(false);
  };

  const savePlan = async () => {
    const v = await form.validateFields();
    if (!items.length) {
      message.warning('请至少添加一条 Launch Plan 明细');
      return;
    }
    for (let i = 0; i < items.length; i++) {
      const row = items[i];
      const rowNo = i + 1;
      if (!row.name?.trim()) {
        message.warning(`第${rowNo}行，机会名称不能为空`);
        return;
      }
      if (!row.targetInboundDate) {
        message.warning(`第${rowNo}行，目标入库时间不能为空`);
        return;
      }
    }
    const payloadItems = items.map(x => ({
      id: x.id || 0,
      name: x.name!,
      brand: x.brand || v.brand || '',
      productLine: x.productLine || '',
      category: x.category || '',
      productType: x.productType || '',
      targetInboundDate: String(x.targetInboundDate).slice(0, 10),
      grade: x.grade || 'S',
      plannedSkuCount: x.plannedSkuCount || '',
      suggestedBriefDate: x.suggestedBriefDate
        ? String(x.suggestedBriefDate).slice(0, 10)
        : null,
      moldType: x.moldType || '待定',
      needCert: x.needCert || '否',
      sourceType: x.sourceType || '日常',
      remark: x.remark || '',
      attachments: (x.attachments || [])
        .filter(a => !!(a.fileUrl || a.downloadUrl))
        .map(a => ({
          fileId: a.fileId || 0,
          fileName: a.fileName,
          fileUrl: a.fileUrl || a.downloadUrl || '',
          fileSize: a.fileSize || 0,
          contentType: a.contentType || '',
        })),
      sourceOpportunityId: x.sourceOpportunityId || 0,
    }));
    setSaving(true);
    try {
      const d = editId
        ? await planVersionApi.update({ id: editId, items: payloadItems })
        : await planVersionApi.create({ brand: v.brand, planYear: v.planYear, items: payloadItems });
      message.success(editId ? '已保存规划明细' : '已新建三年规划（编制中）');
      setEditOpen(false);
      if (planId === d.id) {
        await loadDetail(d.id);
      } else {
        openDetail(d.id);
      }
      void loadList();
    } catch (e: any) {
      message.error(e?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (!detail) return;
    try {
      await planVersionApi.publish(detail.id);
      message.success('已发布；同品牌其它版本已自动归档');
      await loadDetail(detail.id);
      void loadList();
    } catch (e: any) {
      message.error(e?.message || '发布失败');
    }
  };

  const openPick = async () => {
    const brand = form.getFieldValue('brand') as string | undefined;
    if (!brand) {
      message.warning('请先选择品牌');
      return;
    }
    try {
      const res = await opportunityApi.getList({ brand, status: '全部', pageNum: 1, pageSize: 500 });
      setPickRows(res.list || []);
      setPickSelected([]);
      setPickStatusTab('待立项');
      setPickBrand(brand);
      setPickOpen(true);
    } catch (e: any) {
      message.error(e?.message || '加载机会失败');
    }
  };

  const existingNamesInPlan = useMemo(
    () => new Set((items || []).map(x => (x.name || '').trim()).filter(Boolean)),
    [items],
  );

  const existingOppIdsInPlan = useMemo(
    () => new Set((items || []).map(x => x.sourceOpportunityId || 0).filter(id => id > 0)),
    [items],
  );

  const isAlreadyInPlan = (row: OpportunityVO) => {
    const name = (row.name || '').trim();
    if (name && existingNamesInPlan.has(name)) return true;
    if (row.id > 0 && existingOppIdsInPlan.has(row.id)) return true;
    return false;
  };

  const pickFilteredRows = useMemo(() => {
    return (pickRows || []).filter(x => {
      if (pickStatusTab !== '全部' && x.status !== pickStatusTab) return false;
      return true;
    });
  }, [pickRows, pickStatusTab]);

  /** 仅待立项/进行中，且未在本规划中；已完成、已终止、已在规划中均不可引入 */
  const isPickable = (row: OpportunityVO) => {
    if (isAlreadyInPlan(row)) return false;
    if (row.status === '已完成' || row.status === '已终止') return false;
    return row.status === '待立项' || row.status === '进行中';
  };

  const confirmPick = () => {
    const add = pickRows
      .filter(x => pickSelected.includes(x.id) && isPickable(x))
      .map(x => ({
        name: x.name,
        brand: x.brand || pickBrand || watchBrand || '',
        productLine: x.productLine || '',
        category: x.category || '',
        productType: x.productType || '',
        targetInboundDate: x.estimatedInboundDate ? String(x.estimatedInboundDate).slice(0, 10) : undefined,
        grade: x.grade,
        plannedSkuCount: x.plannedSkuCount || '',
        suggestedBriefDate: x.suggestedBriefDate
          ? String(x.suggestedBriefDate).slice(0, 10)
          : undefined,
        moldType: x.moldType || '待定',
        needCert: x.needCert || '否',
        sourceType: x.sourceType || '日常',
        remark: x.remark || '',
        attachments: (x.attachments || [])
          .filter(a => !!(a.fileUrl || a.downloadUrl))
          .map(a => ({
            fileId: a.fileId || 0,
            fileName: a.fileName,
            fileUrl: a.fileUrl || a.downloadUrl || '',
            downloadUrl: a.downloadUrl || a.fileUrl || '',
            fileSize: a.fileSize || 0,
            contentType: a.contentType || '',
          })),
        attachmentCount: x.attachmentCount ?? x.attachments?.length ?? 0,
        sourceOpportunityId: x.id,
      }));
    if (!add.length) {
      message.warning('请勾选可引入的机会（仅待立项/进行中，且未在本规划中）');
      return;
    }
    setItems([...items, ...add]);
    setPickOpen(false);
    message.success(`已引入 ${add.length} 条（快照 + 来源引用，可微调目标入库时间）`);
  };

  const listColumns: ColumnsType<PlanVersionListItem> = [
    { title: '品牌', dataIndex: 'brand', ellipsis: true },
    { title: '规划版本', width: 110, render: (_, r) => `${r.planYear} 版` },
    { title: '覆盖年份', width: 200, ellipsis: true, render: (_, r) => coverYears(r.planYear) },
    { title: 'Launch Plan 项目', dataIndex: 'itemCount', width: 150, render: v => `${v} 项` },
    {
      title: '编制状态',
      dataIndex: 'state',
      width: 110,
      render: v => <IpcStatusBadge variant={planStateVariant(v)}>{v}</IpcStatusBadge>,
    },
    { title: '最近更新', dataIndex: 'updateTime', width: 180, render: v => String(v || '').slice(0, 19) },
    {
      title: '操作',
      width: 140,
      fixed: 'right',
      render: (_, r) => (
        <IpcRowActions
          actions={[
            { key: 'edit', label: '维护', onClick: () => void openEdit(r.id, r.state) },
            { key: 'board', label: '看板', onClick: () => openDetail(r.id) },
          ]}
        />
      ),
    },
  ];

  const patchItem = (i: number, patch: Partial<PlanProjectItem>) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    setItems(next);
  };

  const editColumns: ColumnsType<Partial<PlanProjectItem>> = [
    {
      title: '机会名称',
      width: 160,
      render: (_, __, i) => (
        <Input value={items[i]?.name} onChange={e => patchItem(i, { name: e.target.value })} />
      ),
    },
    {
      title: '品线',
      width: 110,
      render: (_, __, i) => (
        <Input value={items[i]?.productLine} onChange={e => patchItem(i, { productLine: e.target.value })} />
      ),
    },
    {
      title: '品类',
      width: 120,
      render: (_, __, i) => (
        <Input value={items[i]?.category} onChange={e => patchItem(i, { category: e.target.value })} />
      ),
    },
    {
      title: '产品类型',
      width: 110,
      render: (_, __, i) => (
        <Select
          style={{ width: '100%' }}
          value={items[i]?.productType || productTypes[0]}
          options={productTypes.map(g => ({ value: g, label: g }))}
          onChange={v => patchItem(i, { productType: v })}
          getPopupContainer={() => document.body}
        />
      ),
    },
    {
      title: '定级',
      width: 90,
      render: (_, __, i) => (
        <Select
          style={{ width: '100%' }}
          value={items[i]?.grade || 'S'}
          options={grades.map(g => ({ value: g, label: g }))}
          onChange={v => patchItem(i, { grade: v || 'S' })}
          getPopupContainer={() => document.body}
        />
      ),
    },
    {
      title: '计划 SKU 数',
      width: 110,
      render: (_, __, i) => (
        <Input
          value={items[i]?.plannedSkuCount}
          maxLength={50}
          onChange={e => patchItem(i, { plannedSkuCount: e.target.value })}
        />
      ),
    },
    {
      title: '目标入库时间',
      width: 140,
      render: (_, __, i) => (
        <DatePicker
          value={items[i]?.targetInboundDate ? dayjs(items[i].targetInboundDate) : null}
          onChange={d => patchItem(i, { targetInboundDate: d ? d.format('YYYY-MM-DD') : undefined })}
          getPopupContainer={() => document.body}
        />
      ),
    },
    {
      title: '建议 Brief',
      width: 140,
      render: (_, __, i) => (
        <DatePicker
          value={items[i]?.suggestedBriefDate ? dayjs(items[i].suggestedBriefDate) : null}
          onChange={d => patchItem(i, { suggestedBriefDate: d ? d.format('YYYY-MM-DD') : undefined })}
          getPopupContainer={() => document.body}
        />
      ),
    },
    {
      title: '附件',
      width: 88,
      render: (_, __, i) => {
        const row = items[i];
        const attachments = row?.attachments || [];
        return (
          <OpportunityAttachmentCell
            editable
            row={{
              attachmentCount: row?.attachmentCount ?? attachments.length,
              attachments,
            }}
            onChange={next => {
              patchItem(i, {
                attachments: next,
                attachmentCount: next.length,
              });
            }}
            onUploadFiles={async (files) => {
              const added: OpportunityAttachmentVO[] = [];
              for (const file of files) {
                const uploaded = await opportunityApi.uploadToFileSys(file);
                added.push({
                  fileId: uploaded.fileId || 0,
                  fileName: uploaded.fileName,
                  fileUrl: uploaded.fileUrl,
                  downloadUrl: resolveAttachmentDownloadUrl(uploaded.fileUrl),
                  fileSize: uploaded.fileSize,
                  contentType: uploaded.contentType,
                });
              }
              return added;
            }}
          />
        );
      },
    },
    {
      title: '备注',
      width: 160,
      render: (_, __, i) => (
        <Input
          value={items[i]?.remark}
          maxLength={1000}
          placeholder="选填"
          onChange={e => patchItem(i, { remark: e.target.value })}
        />
      ),
    },
    {
      title: '开模',
      width: 90,
      render: (_, __, i) => (
        <Select
          style={{ width: '100%' }}
          value={items[i]?.moldType || '待定'}
          options={molds.map(g => ({ value: g, label: g }))}
          onChange={v => patchItem(i, { moldType: v })}
          getPopupContainer={() => document.body}
        />
      ),
    },
    {
      title: '特证',
      width: 80,
      render: (_, __, i) => (
        <Select
          style={{ width: '100%' }}
          value={items[i]?.needCert || '否'}
          options={regs.map(g => ({ value: g, label: g }))}
          onChange={v => patchItem(i, { needCert: v })}
          getPopupContainer={() => document.body}
        />
      ),
    },
    {
      title: '操作',
      width: 80,
      fixed: 'right',
      render: (_, __, i) => (
        <IpcRowActions
          actions={[{ key: 'remove', label: '移除', danger: true, onClick: () => setItems(items.filter((_, idx) => idx !== i)) }]}
        />
      ),
    },
  ];

  const filteredBoardNodes = useMemo(
    () => filterBoardNodes(board?.nodes || [], boardFilters),
    [board, boardFilters],
  );

  const boardStats = useMemo(() => calcBoardStats(filteredBoardNodes), [filteredBoardNodes]);

  /** 可见时间跨度变化时自动回到「铺满」（时间轴通用 fit-to-width） */
  const boardSpanKey = useMemo(
    () => laneTimeSpanKey(filteredBoardNodes, boardFilters.monthFrom, boardFilters.monthTo),
    [filteredBoardNodes, boardFilters.monthFrom, boardFilters.monthTo],
  );
  const prevBoardSpanKey = useRef<string | null>(null);
  useEffect(() => {
    if (prevBoardSpanKey.current != null && prevBoardSpanKey.current !== boardSpanKey) {
      setLaneZoomPct(100);
    }
    prevBoardSpanKey.current = boardSpanKey;
  }, [boardSpanKey]);

  const clearBoardFilters = () => {
    setBoardFilters(emptyBoardFilters());
  };

  const boardChips: IpcFilterChipItem[] = useMemo(() => {
    const chips: IpcFilterChipItem[] = [];
    if (boardFilters.monthFrom || boardFilters.monthTo) {
      chips.push({
        key: 'month',
        label: `目标入库时间: ${boardFilters.monthFrom || '…'} ~ ${boardFilters.monthTo || '…'}`,
      });
    }
    if (boardFilters.grades?.length) {
      chips.push({ key: 'grades', label: `定级: ${boardFilters.grades.join('、')}` });
    }
    if (boardFilters.stages?.length) {
      chips.push({ key: 'stages', label: `阶段: ${boardFilters.stages.join('、')}` });
    }
    if (boardFilters.delay === 'yes') {
      chips.push({ key: 'delay', label: '延期: 已延期' });
    } else if (boardFilters.delay === 'no') {
      chips.push({ key: 'delay', label: '延期: 未延期' });
    }
    if (boardFilters.ownership === 'in') {
      chips.push({ key: 'ownership', label: '归属: 仅规划内' });
    } else if (boardFilters.ownership === 'out') {
      chips.push({ key: 'ownership', label: '归属: 仅规划外新增' });
    }
    return chips;
  }, [boardFilters]);

  const removeBoardChip = (key: string) => {
    setBoardFilters(prev => {
      const next = { ...prev };
      if (key === 'month') {
        next.monthFrom = undefined;
        next.monthTo = undefined;
      }
      if (key === 'grades') next.grades = undefined;
      if (key === 'stages') next.stages = undefined;
      if (key === 'delay') next.delay = undefined;
      if (key === 'ownership') next.ownership = undefined;
      return next;
    });
  };

  const handleLaneZoomChange = useCallback((z: number) => {
    setLaneZoomPct(Math.round(z * 100));
  }, []);

  const lane = useMemo(() => {
    if (!filteredBoardNodes.length) return null;
    return (
      <PlanLaunchLane
        nodes={filteredBoardNodes}
        monthFrom={boardFilters.monthFrom}
        monthTo={boardFilters.monthTo}
        zoom={laneZoomPct / 100}
        onZoomChange={handleLaneZoomChange}
      />
    );
  }, [filteredBoardNodes, boardFilters.monthFrom, boardFilters.monthTo, laneZoomPct, handleLaneZoomChange]);

  const planEditModal = (
    <>
      <IpcModal
        className="plan-edit-modal"
        title={editId ? '维护三年规划' : '新建三年规划'}
        open={editOpen}
        onCancel={closeEdit}
        onOk={() => void savePlan()}
        confirmLoading={saving}
        okText="保存规划"
        width={1480}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="plan-edit-form">
          <div className="plan-edit-block">
            <div className="plan-edit-block__title">① 头信息（基础信息）</div>
            <div className="plan-edit-form__grid">
              <IpcFormField label="所属品牌" required>
                <Form.Item name="brand" rules={[{ required: true, message: '请选择品牌' }]} noStyle>
                  <Select
                    disabled={!!editId}
                    options={brands}
                    showSearch
                    optionFilterProp="label"
                    placeholder="请选择品牌"
                    getPopupContainer={() => document.body}
                  />
                </Form.Item>
              </IpcFormField>
              <IpcFormField label="规划年份" required>
                <Form.Item
                  name="planYear"
                  rules={[{ required: true, message: '请选择规划年份' }]}
                  noStyle
                  getValueFromEvent={(d: Dayjs | null) => (d?.isValid() ? d.year() : undefined)}
                  getValueProps={(value?: number) => ({
                    value: value ? dayjs(`${value}-01-01`) : undefined,
                  })}
                >
                  <DatePicker
                    picker="year"
                    allowClear={false}
                    disabled={!!editId}
                    placeholder="选择年份"
                    format="YYYY"
                    style={{ width: '100%' }}
                    getPopupContainer={() => document.body}
                  />
                </Form.Item>
              </IpcFormField>
            </div>
            <div className="plan-edit-cover">
              {coverYear} 年制定 → 覆盖三年（含本年）：
              <b>{coverYears(coverYear)}</b>
              （自动生成）
            </div>
          </div>
          <div className="plan-edit-block">
            <div className="plan-edit-block__head">
              <div className="plan-edit-block__title">② 明细 · Launch Plan 项目</div>
              <div className="plan-edit-block__actions">
                <IpcButton
                  variant="secondary"
                  onClick={() => setItems([...items, {
                    name: '',
                    brand: watchBrand || '',
                    productLine: '',
                    category: '',
                    productType: productTypes[0] || '新品',
                    grade: 'S',
                    plannedSkuCount: '',
                    targetInboundDate: `${coverYear + 1}-01-01`,
                    moldType: '待定',
                    needCert: '否',
                    sourceType: '日常',
                    remark: '',
                  }])}
                >
                  手动新增
                </IpcButton>
                <IpcButton variant="primary" onClick={() => void openPick()}>从机会池引入</IpcButton>
              </div>
            </div>
            <IpcTable
              compact
              frameless
              rowKey={(_, i) => String(i)}
              pagination={false}
              columns={editColumns}
              dataSource={items}
              scroll={{ x: 1600, y: 280 }}
              emptyTitle="暂无 Launch Plan 项目"
              emptyDescription="手动新增或从机会池引入"
            />
          </div>
        </Form>
      </IpcModal>

      <IpcModal
        className="plan-pick-modal"
        title={(
          <span className="plan-pick-title">
            从机会池引入 ·
            {' '}
            <b className="plan-pick-title__brand">{pickBrand || '—'}</b>
            <span className="plan-pick-title__hint">仅展示该品牌机会池</span>
          </span>
        )}
        open={pickOpen}
        onCancel={() => setPickOpen(false)}
        onOk={confirmPick}
        width={1100}
        okText="确认引入"
        okDisabled={pickSelected.length === 0}
        footer={(
          <div className="plan-pick-footer">
            <span className="plan-pick-footer__count">
              已选
              {' '}
              <b>{pickSelected.length}</b>
              {' '}
              条
            </span>
            <div className="plan-pick-footer__actions">
              <IpcButton variant="link" onClick={() => setPickOpen(false)}>取消</IpcButton>
              <IpcButton
                variant="primary"
                disabled={pickSelected.length === 0}
                onClick={confirmPick}
              >
                确认引入
              </IpcButton>
            </div>
          </div>
        )}
      >
        <div className="plan-pick-tip">
          可勾选
          {' '}
          <b>待立项 / 进行中</b>
          {' '}
          且未在本规划中的机会。
        </div>
        <div className="plan-pick-toolbar">
          <span className="plan-pick-toolbar__label">状态：</span>
          <div className="plan-pick-seg" role="tablist">
            {PICK_STATUS_TABS.map(tab => (
              <button
                key={tab}
                type="button"
                className={pickStatusTab === tab ? 'is-active' : undefined}
                onClick={() => {
                  setPickStatusTab(tab);
                  setPickSelected([]);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        <div className="plan-pick-table-wrap">
          <IpcTable
            compact
            frameless
            rowKey="id"
            dataSource={pickFilteredRows}
            rowClassName={row => (isPickable(row) ? '' : 'plan-pick-row--muted')}
            rowSelection={{
              selectedRowKeys: pickSelected,
              onChange: keys => setPickSelected(keys as number[]),
              getCheckboxProps: row => ({ disabled: !isPickable(row) }),
            }}
            columns={[
              {
                title: '机会名称',
                dataIndex: 'name',
                width: 180,
                render: (_: string, row) => (
                  <div className="plan-pick-name">
                    <b>{row.name || '—'}</b>
                    <span className="plan-pick-status__note">{row.status || '—'}</span>
                  </div>
                ),
              },
              { title: '品线', dataIndex: 'productLine', width: 110, ellipsis: true },
              { title: '品类', dataIndex: 'category', width: 120, ellipsis: true },
              { title: '产品类型', dataIndex: 'productType', width: 110, ellipsis: true },
              {
                title: '定级',
                dataIndex: 'grade',
                width: 72,
                render: (v: string) => <IpcGradeTag grade={v} />,
              },
              {
                title: '计划 SKU 数',
                dataIndex: 'plannedSkuCount',
                width: 110,
                ellipsis: true,
                render: (v?: string) => v || '—',
              },
              {
                title: '目标入库时间',
                dataIndex: 'estimatedInboundDate',
                width: 120,
                render: v => (v ? String(v).slice(0, 10) : '—'),
              },
              {
                title: '建议 Brief',
                dataIndex: 'suggestedBriefDate',
                width: 120,
                render: v => (v ? String(v).slice(0, 10) : '—'),
              },
              {
                title: '附件',
                dataIndex: 'attachmentCount',
                width: 72,
                render: (_: number, row) => <OpportunityAttachmentCell row={row} />,
              },
              {
                title: '备注',
                dataIndex: 'remark',
                width: 140,
                ellipsis: true,
                render: (v?: string) => v || '—',
              },
              { title: '开模', dataIndex: 'moldType', width: 80 },
              { title: '特证', dataIndex: 'needCert', width: 72 },
            ]}
            pagination={false}
            scroll={{ y: 360, x: 1300 }}
            emptyTitle={pickBrand ? `${pickBrand} 暂无「${pickStatusTab}」机会` : '暂无可引入机会'}
          />
        </div>
      </IpcModal>
    </>
  );

  if (planId != null) {
    if (!detail || detail.id !== planId) {
      return (
        <div className="ipc-page ipc-page-stack opportunity-planning-page" style={{ paddingTop: 48, textAlign: 'center' }}>
          <Spin size="large" tip="加载规划明细..." />
        </div>
      );
    }
    return (
      <div className="ipc-page ipc-page-stack opportunity-planning-page">
        <IpcPageHeader
          title={(
            <span className="plan-detail-title">
              {detail.brand} · {detail.planYear} 版三年规划
              <IpcStatusBadge variant={planStateVariant(detail.state)}>{detail.state}</IpcStatusBadge>
            </span>
          )}
          extra={(
            <>
              <IpcButton variant="secondary" onClick={backToList}>
                返回列表
              </IpcButton>
              <IpcButton
                variant="secondary"
                loading={exportingPlan}
                onClick={() => {
                  void (async () => {
                    setExportingPlan(true);
                    try {
                      await planVersionApi.exportLaunchPlan(detail.id);
                      message.success('导出成功');
                    } catch (e: any) {
                      message.error(e?.message || '导出失败');
                    } finally {
                      setExportingPlan(false);
                    }
                  })();
                }}
              >
                导出
              </IpcButton>
              <IpcButton variant="secondary" onClick={() => void openEdit(detail.id, detail.state)}>
                维护规划
              </IpcButton>
              {detail.state === '编制中' && (
                <IpcButton variant="primary" onClick={() => void publish()}>发布本版</IpcButton>
              )}
            </>
          )}
        >
          <div className="plan-detail-sub">覆盖 {coverYears(detail.planYear)}</div>
        </IpcPageHeader>

        <IpcModuleTabs
          activeKey={detailTab}
          onChange={setDetailTab}
          items={[
            {
              key: 'board',
              label: 'Launch Plan 监控看板',
              children: (
                <div className="plan-board-panel">
                  <div className="plan-board-summary">
                    <div className="plan-board-stats">
                      <div className="plan-board-stat plan-board-stat--in-progress" title="进行中">
                        <span className="plan-board-stat__num">{boardStats.inProgress}</span>
                        <span className="plan-board-stat__lbl">进行中</span>
                      </div>
                      <div className="plan-board-stat plan-board-stat--done">
                        <span className="plan-board-stat__num">{boardStats.completed}</span>
                        <span className="plan-board-stat__lbl">已完成</span>
                      </div>
                      <div className="plan-board-stat plan-board-stat--term">
                        <span className="plan-board-stat__num">{boardStats.terminated}</span>
                        <span className="plan-board-stat__lbl">已终止</span>
                      </div>
                      <div className="plan-board-stat plan-board-stat--notstart">
                        <span className="plan-board-stat__num">{boardStats.notStarted}</span>
                        <span className="plan-board-stat__lbl">待立项</span>
                      </div>
                      <div
                        className="plan-board-stat plan-board-stat--delay"
                        title="延期 = 开发预估入库时间晚于目标入库时间（进行中/逾期交付）；待立项且建议 Brief 已过期（Brief 为空不算）"
                      >
                        <span className="plan-board-stat__num">{boardStats.delayed}</span>
                        <span className="plan-board-stat__lbl">延期</span>
                      </div>
                      <div className="plan-board-stat plan-board-stat--new" title="规划外新增 = 无 related_plan 的 IPD Story（品牌与到仓时间匹配本规划三年窗）">
                        <span className="plan-board-stat__num">{boardStats.outOfPlan}</span>
                        <span className="plan-board-stat__lbl">规划外新增</span>
                      </div>
                    </div>
                    <div className="plan-board-legend">
                      <span><i className="plan-board-legend__dot" style={{ background: STAGE_COLOR['待立项'] }} />待立项</span>
                      <span><i className="plan-board-legend__dot" style={{ background: STAGE_COLOR['进行中'] }} />进行中</span>
                      <span><i className="plan-board-legend__dot" style={{ background: STAGE_COLOR['已完成'] }} />已完成</span>
                      <span><i className="plan-board-legend__dot" style={{ background: STAGE_COLOR['已终止'] }} />已终止</span>
                      <span>
                        <i className="plan-board-legend__delay" />
                        延期：外圈虚线 = 开发预估入库时间晚于目标入库时间；待立项且建议 Brief 已过期（Brief 为空不算）
                      </span>
                      <span className="plan-board-legend__new">角标 (new) = 规划外新增</span>
                    </div>
                  </div>

                  <IpcFilterToolbar
                    showActions={false}
                    chipItems={boardChips}
                    onRemoveChip={removeBoardChip}
                    onClearChips={clearBoardFilters}
                  >
                    <span className="ipc-filter-bar__field ipc-filter-bar__field--range ipc-date-range ipc-date-range--filter">
                      <DatePicker
                        picker="month"
                        allowClear
                        placeholder="目标入库时间起"
                        format="YYYY-MM"
                        value={boardFilters.monthFrom ? dayjs(boardFilters.monthFrom, 'YYYY-MM') : null}
                        onChange={d => setBoardFilters(prev => ({
                          ...prev,
                          monthFrom: d?.isValid() ? d.format('YYYY-MM') : undefined,
                        }))}
                        getPopupContainer={() => document.body}
                      />
                      <span className="ipc-date-range__sep" aria-hidden>~</span>
                      <DatePicker
                        picker="month"
                        allowClear
                        placeholder="目标入库时间止"
                        format="YYYY-MM"
                        value={boardFilters.monthTo ? dayjs(boardFilters.monthTo, 'YYYY-MM') : null}
                        onChange={d => setBoardFilters(prev => ({
                          ...prev,
                          monthTo: d?.isValid() ? d.format('YYYY-MM') : undefined,
                        }))}
                        getPopupContainer={() => document.body}
                      />
                    </span>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="定级"
                      value={boardFilters.grades}
                      options={grades.map(g => ({ value: g, label: g }))}
                      onChange={v => setBoardFilters(prev => ({
                        ...prev,
                        grades: v?.length ? v : undefined,
                      }))}
                      className="ipc-filter-bar__field"
                      optionFilterProp="label"
                      {...IPC_FILTER_MULTI_SELECT_PROPS}
                    />
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="阶段"
                      value={boardFilters.stages}
                      options={[
                        { value: '待立项', label: '待立项' },
                        { value: '进行中', label: '进行中' },
                        { value: '已完成', label: '已完成' },
                        { value: '已终止', label: '已终止' },
                      ]}
                      onChange={v => setBoardFilters(prev => ({
                        ...prev,
                        stages: v?.length ? v : undefined,
                      }))}
                      className="ipc-filter-bar__field"
                      optionFilterProp="label"
                      {...IPC_FILTER_MULTI_SELECT_PROPS}
                    />
                    <Select
                      allowClear
                      placeholder="延期"
                      value={boardFilters.delay}
                      options={[
                        { value: 'yes', label: '已延期' },
                        { value: 'no', label: '未延期' },
                      ]}
                      onChange={v => setBoardFilters(prev => ({ ...prev, delay: v }))}
                      className="ipc-filter-bar__field"
                      getPopupContainer={() => document.body}
                    />
                    <Select
                      placeholder="归属"
                      value={boardFilters.ownership ?? 'all'}
                      options={[
                        { value: 'all', label: '全部' },
                        { value: 'out', label: '仅规划外新增' },
                        { value: 'in', label: '仅规划内' },
                      ]}
                      onChange={v => setBoardFilters(prev => ({
                        ...prev,
                        ownership: v === 'all' ? undefined : v,
                      }))}
                      className="ipc-filter-bar__field"
                      getPopupContainer={() => document.body}
                    />
                  </IpcFilterToolbar>

                  <div className="plan-board-lane-card">
                    <div className="plan-board-lane-card__title">
                      <span className="plan-board-lane-card__heading">Launch Plan 泳道 · 品线 × 时间</span>
                      <div className="plan-board-lane-card__tools">
                        <div className="pv-zoom-bar" title="也可在泳道上按住 Ctrl（Mac：⌘）+ 滚轮缩放">
                          <span className="pv-zoom-bar__label">缩放</span>
                          <Slider
                            className="pv-zoom-bar__slider"
                            min={50}
                            max={300}
                            step={10}
                            value={laneZoomPct}
                            onChange={setLaneZoomPct}
                            tooltip={{ formatter: v => `${v}%` }}
                          />
                          <IpcButton variant="secondary" size="small" onClick={() => setLaneZoomPct(100)}>
                            重置
                          </IpcButton>
                        </div>
                        <span className="plan-board-lane-card__count">共 {filteredBoardNodes.length} 项</span>
                      </div>
                    </div>
                    {lane || <div className="plan-board-empty">当前筛选条件下暂无项目</div>}
                  </div>
                </div>
              ),
            },
            {
              key: 'lp',
              label: 'Launch Plan',
              children: (
                <IpcTable
                  compact
                  frameless
                  rowKey={r => (r.id > 0 ? String(r.id) : `new-${r.matchedWorkItemIds?.[0] || r.name}`)}
                  dataSource={detail.items || []}
                  pagination={false}
                  columns={[
                    {
                      title: '产品名称',
                      dataIndex: 'name',
                      width: 240,
                      className: 'plan-name-col',
                      render: (v, r) => (
                        <span className="plan-name-cell">
                          {v}
                          {r.isNew ? <span className="plan-board-legend__new" style={{ marginLeft: 6 }}>(new)</span> : null}
                        </span>
                      ),
                    },
                    {
                      title: '品线',
                      dataIndex: 'productLine',
                      width: 110,
                      ellipsis: true,
                      render: (v, r) => (r.isNew ? '规划外' : (v || '—')),
                    },
                    { title: '品类', dataIndex: 'category', width: 120, ellipsis: true, render: (v?: string) => v || '—' },
                    { title: '产品类型', dataIndex: 'productType', width: 110, ellipsis: true, render: (v?: string) => v || '—' },
                    { title: '定级', dataIndex: 'grade', width: 72, render: (v: string) => <IpcGradeTag grade={v} /> },
                    {
                      title: '计划 SKU 数',
                      dataIndex: 'plannedSkuCount',
                      width: 110,
                      ellipsis: true,
                      render: (v?: string) => v || '—',
                    },
                    {
                      title: '目标入库时间',
                      dataIndex: 'targetInboundDate',
                      width: 120,
                      render: v => (v ? String(v).slice(0, 10) : '—'),
                    },
                    {
                      title: '建议 Brief',
                      dataIndex: 'suggestedBriefDate',
                      width: 120,
                      render: v => (v ? String(v).slice(0, 10) : '—'),
                    },
                    { title: '开模', dataIndex: 'moldType', width: 80, render: (v?: string) => v || '—' },
                    { title: '特证', dataIndex: 'needCert', width: 72, render: (v?: string) => v || '—' },
                    { title: '来源', dataIndex: 'sourceType', width: 80, render: (v?: string) => v || '—' },
                    {
                      title: '附件',
                      dataIndex: 'attachmentCount',
                      width: 72,
                      render: (_: number, row) => <OpportunityAttachmentCell row={row} />,
                    },
                    {
                      title: '状态',
                      dataIndex: 'status',
                      width: 140,
                      render: (_: string, r) => {
                        const status = r.status || r.stage || '待立项';
                        return (
                          <PlanningLinkStatusHover
                            status={status}
                            linkedIpds={r.linkedIpds}
                            linkedIpcs={r.linkedIpcs}
                          >
                            <span className="plan-stage-cell">
                              <IpcStatusBadge
                                variant={
                                  status === '已完成' ? 'success'
                                    : status === '进行中' || r.isNew ? 'primary'
                                      : status === '已终止' ? 'destructive'
                                        : 'muted'
                                }
                              >
                                {status}
                              </IpcStatusBadge>
                              {(r.delayMonths || 0) > 0 && (
                                <IpcStatusBadge variant="warning">延期 {r.delayMonths} 月</IpcStatusBadge>
                              )}
                            </span>
                          </PlanningLinkStatusHover>
                        );
                      },
                    },
                  ]}
                  emptyTitle="暂无 Launch Plan"
                  className="opportunity-planning-table"
                  scroll={{ x: 'max-content' }}
                  autoHeight
                  autoHeightDeps={[(detail.items || []).length, detailTab]}
                  panelClassName="opportunity-table-panel"
                  scrollClassName={[
                    'opportunity-table-scroll',
                    !(detail.items || []).length ? 'ipc-empty-host' : '',
                  ].filter(Boolean).join(' ')}
                />
              ),
            },
          ]}
        />
        {planEditModal}
      </div>
    );
  }

  return (
    <div className="ipc-page ipc-page-stack opportunity-planning-page">
      <IpcFilterToolbar
        onSubmit={submitFilters}
        onReset={resetFiltersToDefault}
        chipItems={appliedChips}
        onRemoveChip={removeChip}
        onClearChips={resetFiltersToDefault}
        extra={(
          <IpcButton variant="primary" icon={<Plus size={14} />} onClick={openCreate}>
            新建三年规划
          </IpcButton>
        )}
      >
        <Select
          allowClear
          placeholder="全部品牌"
          value={draftBrand}
          options={brands}
          onChange={v => setDraftBrand(v)}
          className="ipc-filter-bar__field"
          showSearch
          optionFilterProp="label"
          getPopupContainer={() => document.body}
        />
        <Select
          allowClear
          placeholder="编制状态"
          value={draftState}
          options={[
            { value: '编制中', label: '编制中' },
            { value: '已发布', label: '已发布' },
            { value: '已归档', label: '已归档' },
          ]}
          onChange={v => setDraftState(v)}
          className="ipc-filter-bar__field"
          getPopupContainer={() => document.body}
        />
      </IpcFilterToolbar>

      <IpcTable<PlanVersionListItem>
        compact
        frameless
        resizable
        rowKey="id"
        loading={loading}
        columns={listColumns}
        dataSource={filteredList}
        tableLayout="fixed"
        className="opportunity-planning-table"
        scroll={{ x: 'max-content' }}
        autoHeight
        autoHeightDeps={[filteredList.length, loading]}
        panelClassName="opportunity-table-panel"
        scrollClassName={[
          'opportunity-table-scroll',
          filteredList.length === 0 && !loading ? 'ipc-empty-host' : '',
        ].filter(Boolean).join(' ')}
        pagination={false}
        emptyTitle="暂无三年规划"
        emptyDescription="请调整筛选条件或新建三年规划"
      />
      {planEditModal}
    </div>
  );
};

export default ThreeYearPlan;
